import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Calendar,
  Edit3,
  Eye,
  FileText,
  Hash,
  Image as ImageIcon,
  ImageOff,
  Loader2,
  Newspaper,
  Save,
  Sparkles,
  Tag,
  Type,
  User,
} from "lucide-react";
import type {
  Article,
  ArticleDate,
  UpdateArticle,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WysiwygEditor } from "@/components/wysiwyg-editor";
import { toast } from "@/components/ui/sonner";

function dateString(v: ArticleDate | undefined): string | undefined {
  if (v == null) return undefined;
  return typeof v === "string" ? v : v.$date;
}

function formatDate(
  v: ArticleDate | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  const s = dateString(v);
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", options);
}

const editableFields = ["title", "content", "description"] as const;

export default function ArticleEditor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id = "" } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ["il-website", "article", id],
    queryFn: () => api.ilWebsite.getArticle(id),
    enabled: !!id,
  });

  const [article, setArticle] = useState<Article | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (query.data) setArticle(query.data);
  }, [query.data]);

  const saveArticle = useMutation({
    mutationFn: (changes: UpdateArticle) =>
      api.ilWebsite.updateArticle(id, changes),
    onSuccess: () => {
      toast.success("Article saved");
      void queryClient.invalidateQueries({
        queryKey: ["il-website", "articles"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["il-website", "article", id],
      });
      navigate("/dashboard/il-website/articles-editor");
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to save article"),
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (query.isError || !article) {
    return (
      <div className="space-y-4">
        <Link
          to="/dashboard/il-website/articles-editor"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to articles
        </Link>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : "Failed to load article"}
        </div>
      </div>
    );
  }

  const update = <K extends keyof Article>(field: K, value: Article[K]) =>
    setArticle((prev) => (prev ? { ...prev, [field]: value } : prev));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!article) return;
    const changes: UpdateArticle = {};
    for (const f of editableFields) {
      changes[f] = (article[f] as string | undefined) ?? "";
    }
    saveArticle.mutate(changes);
  };

  const wordCount = (article.content ?? "")
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;

  return (
    <form onSubmit={handleSubmit} dir="rtl" className="space-y-6">
      <div className="sticky top-0 z-10 -mx-6 flex items-center justify-between bg-background/95 px-6 py-3 backdrop-blur">
        <Button
          asChild
          variant="ghost"
          size="sm"
          type="button"
        >
          <Link to="/dashboard/il-website/articles-editor">
            <ArrowLeft className="h-4 w-4" />
            Back to list
          </Link>
        </Button>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border bg-muted/50 p-1">
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                !showPreview
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Edit3 className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                showPreview
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
          </div>
          <Button type="submit" disabled={saveArticle.isPending}>
            {saveArticle.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {showPreview ? (
        <FullPreview article={article} />
      ) : (
        <>
          <ReadOnlyInfo article={article} />

          <Section icon={Type} title="Title">
            <Input
              dir="rtl"
              value={article.title ?? ""}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Article title"
              className="h-12 text-xl font-semibold"
            />
          </Section>

          <Section
            icon={FileText}
            title="Description"
            badge={`${(article.description ?? "").length} chars`}
          >
            <WysiwygEditor
              value={article.description ?? ""}
              onChange={(v) => update("description", v)}
              minHeight={100}
            />
          </Section>

          <Section icon={Sparkles} title="Story Highlights">
            {article.storyHighlights ? (
              <div
                className="prose prose-sm dark:prose-invert max-w-none rounded-lg border bg-muted/30 p-4"
                dir="rtl"
                dangerouslySetInnerHTML={{ __html: article.storyHighlights }}
              />
            ) : (
              <p className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                No story highlights
              </p>
            )}
          </Section>

          <Section
            icon={FileText}
            title="Content"
            badge={`${wordCount} words`}
          >
            <WysiwygEditor
              value={article.content ?? ""}
              onChange={(v) => update("content", v)}
              minHeight={400}
            />
          </Section>

          <Section icon={ImageIcon} title="Images">
            <div className="grid gap-6 lg:grid-cols-2">
              <ImageBlock
                label="Main image"
                src={article.image?.src}
                width={article.image?.width}
                height={article.image?.height}
                size="large"
              />
              <ImageBlock
                label="Thumbnail"
                src={article.thumbnail?.src}
                size="small"
              />
            </div>
          </Section>
        </>
      )}
    </form>
  );
}

function ReadOnlyInfo({ article }: { article: Article }) {
  return (
    <Section icon={Hash} title="Article Information">
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <InfoCard label="ID" value={String(article._id)} mono />
        <InfoCard
          label="Created"
          icon={Calendar}
          value={formatDate(article._u?.i)}
        />
        <InfoCard
          label="Updated"
          icon={Calendar}
          value={formatDate(article._u?.u)}
        />
        <InfoCard
          label="Author"
          icon={User}
          value={article.author || "—"}
        />
        <InfoCard
          label="Category"
          icon={Tag}
          value={article.category || "—"}
        />
        <InfoCard
          label="Slug"
          icon={Hash}
          value={article.slug || "—"}
          mono
        />
      </div>
    </Section>
  );
}

function InfoCard({
  label,
  value,
  icon: Icon,
  mono,
}: {
  label: string;
  value: string;
  icon?: React.ElementType;
  mono?: boolean;
}) {
  return (
    <div className="rounded-lg bg-muted/50 p-3">
      <div className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <p className={`text-sm ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  badge,
  children,
}: {
  icon: React.ElementType;
  title: string;
  badge?: string | number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">{title}</h3>
        {badge !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function ImageBlock({
  label,
  src,
  width,
  height,
  size,
}: {
  label: string;
  src?: string;
  width?: number;
  height?: number;
  size: "small" | "large";
}) {
  const [error, setError] = useState(false);
  const dim = size === "small" ? 80 : 200;

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex gap-4">
        {!src ? (
          <Placeholder dim={dim} label="No image" />
        ) : error ? (
          <Placeholder dim={dim} label="Failed" error />
        ) : (
          <img
            src={src}
            alt={label}
            onError={() => setError(true)}
            className="rounded-lg border object-cover"
            style={{ maxWidth: dim * 1.5, maxHeight: dim }}
          />
        )}
        <div className="flex-1 space-y-1">
          <p className="truncate rounded-lg bg-muted/50 p-2 font-mono text-xs text-muted-foreground">
            {src || "—"}
          </p>
          {width && height && (
            <p className="text-xs text-muted-foreground">
              {width} × {height}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Placeholder({
  dim,
  label,
  error,
}: {
  dim: number;
  label: string;
  error?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed text-xs ${
        error
          ? "border-destructive/25 bg-destructive/5 text-destructive"
          : "border-muted-foreground/25 bg-muted/50 text-muted-foreground"
      }`}
      style={{ width: dim, height: dim }}
    >
      <ImageOff className="h-5 w-5" />
      {label}
    </div>
  );
}

function FullPreview({ article }: { article: Article }) {
  return (
    <div className="rounded-lg border bg-card">
      <div className="rounded-t-lg bg-gradient-to-r from-primary/10 to-primary/5 px-6 py-3 text-sm text-muted-foreground">
        <Newspaper className="mr-2 inline h-4 w-4 align-middle" />
        Article Preview
      </div>
      <div className="p-6">
        {article.image?.src && (
          <div className="mb-6 overflow-hidden rounded-lg">
            <img
              src={article.image.src}
              alt={article.title}
              className="w-full object-cover"
              style={{ maxHeight: 400 }}
            />
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
          {article.category && (
            <span className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
              {article.category}
            </span>
          )}
          {article.date && (
            <span className="text-muted-foreground">
              {formatDate(article.date, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          )}
          {(article.authorTranslatedName ?? article.author) && (
            <span className="text-muted-foreground">
              by{" "}
              <span className="font-medium">
                {article.authorTranslatedName ?? article.author}
              </span>
            </span>
          )}
        </div>
        <h1 className="mb-4 text-3xl font-bold leading-tight" dir="rtl">
          {article.title || (
            <span className="text-muted-foreground">No title</span>
          )}
        </h1>
        {article.description && (
          <p className="mb-6 text-lg text-muted-foreground" dir="rtl">
            {article.description}
          </p>
        )}
        {article.content ? (
          <div
            className="prose prose-lg dark:prose-invert max-w-none"
            dir="rtl"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        ) : (
          <p className="text-muted-foreground">No content</p>
        )}
      </div>
    </div>
  );
}
